import { HttpErrorResponse } from '@angular/common/http';
import { inject, Service, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subscription } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import {
  AddReservationModel,
  CartService,
  ItemRecommendationDto,
  ItemsService,
  ReservationCreatedDtoIEnumerableResultDto,
  ReservationEntryType,
} from 'src/api';
import { openApiNumberArrayParam } from 'src/api/utils/api-workarounds';
import {
  DialogOrderFieldsComponentData,
  DialogOrderFieldsResult,
  ItemCustomFieldsDialogComponent,
} from 'src/app/generic-fields/item-custom-fields-dialog/item-custom-fields-dialog.component';
import {
  selectCatalog,
  selectCatalogSettings,
  selectCatName,
} from 'src/app/root-store/catalog-store/selectors';
import { DialogService, panelClassMobileFullscreen } from 'src/app/services/dialog.service';
import { LocaleService } from '../core/locale/locale.service';
import { OverlappingsError, ReservationErrorTypeKey } from '../models/reservation-errors';
import { ReloadAppointments } from '../root-store/actions';
import { SetTreeItems, SetTreeItemsLoading } from '../root-store/object-tree-store/actions';
import {
  selectObjectTree,
  selectObjectTreeErrors,
  selectSelectedItems,
} from '../root-store/object-tree-store/selects';
import { RootState } from '../root-store/root-state.model';
import { NavigateTo } from '../root-store/router/actions';
import { selectOrderId } from '../root-store/router/selectors';
import { selectSchedulerErrors } from '../root-store/scheduler-store/selectors';
import { SetRecurrenceFreq } from '../root-store/time-picker-store/actions';
import {
  selectRecurrenceRule,
  selectTimePicker,
  selectTimePickerErrors,
} from '../root-store/time-picker-store/selectors';
import { selectUser } from '../root-store/user-store/selectors';
import { ErrorHandlingService } from '../services/error-handling.service';
import { ItemTreeService } from '../services/item-tree.service';
import { ItemService } from '../services/item.service';
import { ReservationErrorService } from '../services/reservation-error.service';
import { ConfirmDialogService } from '../shared/confirm-dialog/confirm-dialog.service';
import { AdvancedOptionsForm } from './advanced-options/advanced-options.component';
import {
  RecommendedItemsDialogComponent,
  RecommendedItemsDialogData,
  RecommendedItemsDialogResult,
} from './recommended-items-dialog/recommended-items-dialog.component';

@Service()
export class ObjectChoiceService {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly translate = inject(TranslateService);
  private readonly localeService = inject(LocaleService);
  private readonly dialog = inject(MatDialog);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly cartService = inject(CartService);
  private readonly itemTreeService = inject(ItemTreeService);
  private readonly itemsApiService = inject(ItemsService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly errorHandlingService = inject(ErrorHandlingService);
  private readonly reservationErrorService = inject(ReservationErrorService);
  private readonly router = inject(Router);
  private readonly dialogSvc = inject(DialogService);

  isSubmitting = signal(false);
  allowAddToCart$: Observable<boolean>;
  allowAddToCart: Signal<boolean>;
  hasErrors$: Observable<boolean>;
  hasErrors: Signal<boolean>;
  hasItemsSelected$ = this.store.select(selectSelectedItems).pipe(map((i) => i.length > 0));
  smallScreenStepIndex = signal(1);
  private selectedItems = this.store.selectSignal(selectSelectedItems);
  private persistItemsTreeSelection = false;
  private persistItemsTreeStorageKey: string | undefined;
  public advancedOptions?: FormGroup<AdvancedOptionsForm>;
  public isBlock$ = new BehaviorSubject<boolean>(false);
  public isBlock = toSignal(this.isBlock$, { initialValue: this.isBlock$.value });
  public showRootIds: number[] | undefined;
  navigateEndSubs?: Subscription;
  private settings = this.store.selectSignal(selectCatalogSettings);
  private user = this.store.selectSignal(selectUser);

  constructor() {
    this.hasErrors$ = combineLatest([
      this.store.select(selectSelectedItems),
      this.store.select(selectObjectTreeErrors),
      this.store.select(selectSchedulerErrors),
      this.store.select(selectTimePickerErrors),
    ]).pipe(
      map(
        ([items, treeErrors, schedulerErrors, timeErrors]) =>
          items.length === 0 ||
          Object.keys({ ...treeErrors, ...timeErrors }).length > 0 ||
          schedulerErrors.length > 0,
      ),
    );

    this.hasErrors = toSignal(this.hasErrors$, { initialValue: true });

    this.allowAddToCart$ = combineLatest([
      this.store.select(selectUser),
      this.store.select(selectSelectedItems),
      this.store.select(selectObjectTreeErrors),
      this.store.select(selectSchedulerErrors),
      this.store.select(selectTimePickerErrors),
      this.isBlock$,
    ]).pipe(
      map(([user, items, treeErrors, schedulerErrors, timeErrors, isBlock]) => {
        if (user.isAtLeastAdmin) {
          return true;
        }

        if (items.length === 0 || treeErrors || timeErrors) {
          return false;
        }

        if (schedulerErrors.length && !isBlock) {
          // if all scheduler-errors have couldBypass=true, allow 'add to cart'
          return !schedulerErrors.some((e) => e.reservationError?.metadata?.couldBypass !== true);
        }

        return true;
      }),
    );

    this.allowAddToCart = toSignal(this.allowAddToCart$, { initialValue: false });

    // must use observable to get CatalogSettings because ObjectChoiceService is created before the catalog is loaded
    this.store
      .select(selectCatalogSettings)
      .pipe(
        filter((s) => !!s),
        take(1),
      )
      .subscribe(
        (settings) => (this.persistItemsTreeSelection = settings.PersistItemsTreeSelection),
      );

    this.store
      .select(selectCatName)
      .subscribe((name) => (this.persistItemsTreeStorageKey = name + '.ItemsTree.SelectedItemIds'));
  }

  async saveReservation(
    customFieldsOfAllItems?: Observable<AddReservationModel['customFieldsOfAllItems']>,
  ) {
    const isBlock = this.isBlock$.value;
    const selectedItems = this.selectedItems();

    // check if booking is allowed.
    // SaveReservation() should be never called if not allowed, because button is disabled if there are errors and user has no permissions
    if (this.hasErrors() && !(await this.confirmContinueWithErrors())) {
      return;
    }

    // optionally get additional Object-OrderFields from the DialogOrderFieldsComponent
    let orderFields$: Observable<AddReservationModel['customFieldsOfAllItems']> =
      customFieldsOfAllItems || of({});
    if (!customFieldsOfAllItems && !isBlock) {
      const itemsWithOrderFields = selectedItems.filter((i) => i.item.fields.length > 0);
      if (itemsWithOrderFields.length) {
        const dialogOrderFieldsComponent = (
          await import('../generic-fields/item-custom-fields-dialog/item-custom-fields-dialog.component')
        ).ItemCustomFieldsDialogComponent;

        orderFields$ = this.dialog
          .open<
            ItemCustomFieldsDialogComponent,
            DialogOrderFieldsComponentData,
            DialogOrderFieldsResult
          >(dialogOrderFieldsComponent, {
            data: {
              mode: 'new',
              items: itemsWithOrderFields,
            },
            panelClass: panelClassMobileFullscreen,
          })
          .afterClosed()
          .pipe(
            // if user closed the dialog with 'cancel', the result will be undefined. In that case, we don't want to proceed with the reservation,
            // so we filter out undefined results here
            filter((res) => !!res),
          );
      }
    }

    // save reservation and handle response
    orderFields$
      .pipe(
        tap(() => this.isSubmitting.set(true)),
        switchMap((customFieldsOfAllItems) =>
          this.saveReservationToBackend(customFieldsOfAllItems),
        ),
      )
      .subscribe({
        next: async (saveResult) => {
          this.isSubmitting.set(false);

          const orderId = this.store.selectSignal(selectOrderId)();

          if (!isBlock || orderId) {
            const addToReservation = saveResult.content?.find(
              (r) => r.entryType == ReservationEntryType.Reservation && !r.reservationGroupId,
            );

            (await this.selectRecommendedItems(addToReservation?.reservationId)).subscribe(() =>
              this.store.dispatch(NavigateTo({ route: 'Cart', queryParams: { OrderId: orderId } })),
            );
          } else {
            this.advancedOptions?.reset();
            this.store.dispatch(SetRecurrenceFreq({ recurrenceFreq: null }));
            this.store.dispatch(ReloadAppointments({ ignoreSchedulerErrors: true }));
            this.snackBar.open(this.translate.instant('BLOCK_CREATED'), undefined, {
              duration: 5000,
            });
          }
        },
        error: (error) => {
          this.isSubmitting.set(false);

          // snackbar for 403 will be shown by WebApiInterceptor, so we don't show it here again
          if (error instanceof HttpErrorResponse && error.status === 403) {
            return;
          }

          const reservationErrorMsg =
            this.reservationErrorService.tryGetReservationErrorMessage(error);
          if (reservationErrorMsg) {
            this.errorHandlingService.showError(reservationErrorMsg, 10000);
          } else {
            this.errorHandlingService.handleApiError(error);
          }

          this.store.dispatch(ReloadAppointments()); // reload appointments, to ensure current appointments are shown
        },
      });
  }

  // build a confirmation dialog to ask the user if he wants to continue with errors
  public async confirmContinueWithErrors(isEditInCart = false): Promise<boolean> {
    const reservationErrors = this.store.selectSignal(selectSchedulerErrors)();
    const isOverlappingError =
      reservationErrors.length === 1 &&
      reservationErrors[0].reservationError?.metadata?.errorType ===
      ReservationErrorTypeKey.OverlappingsError;

    let confirmTitle: string = _('OBJECT_CHOICE.CONTINUE_WITH_ERRORS');
    let confirmMessage = this.translate.instant('OBJECT_CHOICE.CONTINUE_WITH_ERRORS_MESSAGE');

    // Janitor can continue with almost any errors
    if (this.user().isAtLeastJanitor) {
      if (reservationErrors.length) {
        const includeItemNames = this.selectedItems().length > 1;
        confirmMessage = this.reservationErrorService
          .getErrorMessages(reservationErrors, includeItemNames)
          .join('\n');
      }

      confirmMessage +=
        '<br>' + this.translate.instant('OBJECT_CHOICE.CONTINUE_WITH_ERRORS_AS_JANITOR');
    }
    // customers trying to create overlapping reservations will get a different message, if they have the permission to create recurring reservations
    else {
      if (isOverlappingError && this.user().permissions.createRecurringReservationsOnItemIds) {
        const overlappingError = reservationErrors[0].reservationError as OverlappingsError;

        confirmTitle =
          overlappingError.metadata.overlappingsCount === 1
            ? _('OBJECT_CHOICE.CONTINUE_WITH_OVERLAPPING')
            : _('OBJECT_CHOICE.CONTINUE_WITH_OVERLAPPINGS');

        confirmMessage = isEditInCart
          ? _('OBJECT_CHOICE.OVERLAPPINGS_MUST_BE_FIXED')
          : _('OBJECT_CHOICE.FIX_OVERLAPPINGS_IN_NEXT_STEP');
      }
    }

    return await this.confirmDialogService.openAsPromise(
      confirmTitle,
      confirmMessage,
      _('CANCEL'),
      _('CONTINUE'),
    );
  }

  private saveReservationToBackend(
    customFieldsOfAllItems: AddReservationModel['customFieldsOfAllItems'],
  ): Observable<ReservationCreatedDtoIEnumerableResultDto> {
    const timePicker = this.store.selectSignal(selectTimePicker)();
    const objectTree = this.store.selectSignal(selectObjectTree)();

    if (!timePicker.startDate || !timePicker.endDate) {
      return of({
        isSuccess: false,
        errors: [{ message: 'StartEndMissing' }],
      } satisfies ReservationCreatedDtoIEnumerableResultDto);
    }

    // prepare parameters for backend call
    const itemIds = openApiNumberArrayParam(objectTree.selectedItems.map((i) => i.itemId));
    const start = timePicker.startDate.toDate();
    const end = timePicker.endDate.toDate();
    const recurrence = timePicker.recurrenceRule ?? undefined;
    const comment = this.advancedOptions?.controls.comment.value ?? undefined;
    const externalComment = this.advancedOptions?.controls.externalComment.value ?? undefined;
    const availabilityPlanId = timePicker.availabilityPlanId ?? undefined;
    const quantity =
      timePicker.quantity && timePicker.quantity > 1 ? timePicker.quantity : undefined;

    // Save as block reservation
    if (this.isBlock$.value) {
      const doubleReservationAllowed =
        this.advancedOptions?.controls.doubleReservationAllowed.value ?? false;
      const mailRecipientOnOverlappingWithBlockInfo =
        doubleReservationAllowed && this.advancedOptions?.controls.mailOverlappingBlock.value
          ? this.advancedOptions.controls.mailOverlappingBlock.value
          : undefined;

      return this.cartService.addBlock(
        itemIds,
        start,
        end,
        comment,
        externalComment,
        availabilityPlanId,
        doubleReservationAllowed,
        mailRecipientOnOverlappingWithBlockInfo,
        recurrence,
        quantity,
      );
    }
    // Save as normal reservation
    else {
      const requestBody = { customFieldsOfAllItems } satisfies AddReservationModel;

      return this.cartService.addReservation(
        requestBody,
        itemIds,
        start,
        end,
        recurrence,
        comment,
        externalComment,
        availabilityPlanId,
        quantity,
      );
    }
  }

  public get PersistentSelectedItemIds(): number[] | null {
    return this.PersistItemsTreeSelectionEnabled
      ? (localStorage.getItem(this.persistItemsTreeStorageKey!)?.split(',').map(Number) ?? null)
      : null;
  }

  public set PersistentSelectedItemIds(ids: number[] | null) {
    if (this.PersistItemsTreeSelectionEnabled) {
      localStorage.setItem(this.persistItemsTreeStorageKey!, ids?.join(',') ?? '');
    }
  }

  public get PersistItemsTreeSelectionEnabled(): boolean {
    return this.user().isAtLeastJanitor && this.persistItemsTreeSelection;
  }

  public loadTreeItems(store: Store<RootState>) {
    store.dispatch(SetTreeItemsLoading());

    return this.itemTreeService
      .getItemTree()
      .subscribe((items) => store.dispatch(SetTreeItems({ items })));
  }

  public initItemInfoDialog() {
    // open object-info-dialog on initial load (if needed)
    this.openItemInfo(ItemService.getItemFromUrl(this.router.url)?.id);

    // open object-info-dialog on route-changes (if needed)
    if (!this.navigateEndSubs) {
      this.navigateEndSubs = this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(
          async (val) =>
            await this.openItemInfo(ItemService.getItemFromUrl((val as NavigationEnd).url)?.id),
        );
    }
  }

  // open object-info-dialog
  private async openItemInfo(itemId: number | undefined) {
    if (!itemId) {
      return;
    }

    const catalog = this.store.selectSignal(selectCatalog)();

    (await this.dialogSvc.openObjectInfo(itemId, true)).afterClosed().subscribe((closeResult) => {
      if (!closeResult?.redirectedToNewSeoName) {
        this.router.navigate(['/' + catalog.name, this.localeService.currentLang()]);
      }
    });
  }

  private async selectRecommendedItems(
    accessoryOfReservationId?: number,
  ): Promise<Observable<ReservationCreatedDtoIEnumerableResultDto | undefined>> {
    if (accessoryOfReservationId) {
      const selectedItems = this.store.selectSignal(selectObjectTree)().selectedItems;
      const recurrenceRule = this.store.selectSignal(selectRecurrenceRule)();

      if (!recurrenceRule) {
        const selectedItemIds = selectedItems.map((i) => i.itemId);
        const selectedItemNames = selectedItems.map((i) => i.item.name);

        const timePickerValues = this.store.selectSignal(selectTimePicker)();

        const recommendedAccessory = await firstValueFrom(
          this.itemsApiService.getRecommendedItems(openApiNumberArrayParam(selectedItemIds), true, timePickerValues.availabilityPlanId ?? undefined),
        );

        if (recommendedAccessory.length) {
          return await this.showRecommendedItemsDialog(
            selectedItemNames,
            accessoryOfReservationId,
            recommendedAccessory,
          );
        }
      }
    }

    return of(undefined);
  }

  private async showRecommendedItemsDialog(
    itemName: string[],
    accessoryOfReservationId: number,
    recommendedItems: ItemRecommendationDto[],
  ): Promise<Observable<ReservationCreatedDtoIEnumerableResultDto | undefined>> {
    const recommendedItemsComponent = (
      await import('../object-choice/recommended-items-dialog/recommended-items-dialog.component')
    ).RecommendedItemsDialogComponent;

    const dialogRef = this.dialog.open<
      RecommendedItemsDialogComponent,
      RecommendedItemsDialogData,
      RecommendedItemsDialogResult
    >(recommendedItemsComponent, {
      panelClass: panelClassMobileFullscreen,
      autoFocus: false,
      restoreFocus: false,
      width: '800px',
      data: {
        itemName,
        dialogMode: 'AddToCart',
        accessoryOfReservationId,
        recommendedItems,
      },
    });

    return dialogRef.afterClosed();
  }
}
